import { Search } from 'lucide-react'
import type * as React from 'react'
import { cn } from '../lib/cn'
import { Kbd } from './kbd'

export function Wordmark({ className }: { className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-2 text-lg font-bold tracking-tight text-foreground', className)}>
      <svg viewBox="0 0 24 24" aria-hidden className="size-6 text-primary" fill="none">
        <rect x="2" y="2" width="20" height="20" rx="5" fill="currentColor" />
        <circle cx="8" cy="9" r="1.8" fill="white" />
        <circle cx="16" cy="9" r="1.8" fill="white" />
        <circle cx="8" cy="15" r="1.8" fill="white" />
        <circle cx="16" cy="15" r="1.8" fill="white" />
        <path d="M8 9 C 12 9, 12 15, 16 15" stroke="white" strokeWidth="1.6" />
      </svg>
      <span className="max-pane:sr-only">Patchbay</span>
    </span>
  )
}

export type Liveness = 'fresh' | 'lagging' | 'stale'

export function livenessOf(ageMinutes: number | null): Liveness {
  if (ageMinutes === null || ageMinutes >= 60) return 'stale'
  if (ageMinutes >= 15) return 'lagging'
  return 'fresh'
}

export function formatAge(ageMinutes: number): string {
  if (ageMinutes < 1) return 'just now'
  if (ageMinutes < 60) return `${Math.round(ageMinutes)} min ago`
  const h = Math.floor(ageMinutes / 60)
  return `${h} h ago`
}

/**
 * Desk-event liveness (scope §5.0): green, amber at 15 min, red at 60 min. The red state also
 * disables sends; the shell shows the "Mirror stale; sends disabled" banner. `state` lets the API's own
 * verdict (its thresholds, `sends_disabled`) win over the age; `unknown` says the age could not be read,
 * which is shown red, never as "never" (P11).
 */
export function LivenessPill({
  ageMinutes,
  source = 'Desk events',
  state: stateProp,
  unknown = false,
  quietSince,
  label: labelProp,
}: {
  ageMinutes: number | null
  source?: string
  state?: Liveness
  unknown?: boolean
  /** "14:05": the API says the mirror is fine and Desk is just quiet. Shown calmly instead of the age. */
  quietSince?: string
  /** Connection-state override such as "Reconnecting…"; colour still comes from `state`. */
  label?: string
}) {
  const state = unknown ? 'stale' : (stateProp ?? livenessOf(ageMinutes))
  const label =
    labelProp ??
    (unknown
      ? `${source}: unknown`
      : quietSince && state === 'fresh'
        ? `Quiet since ${quietSince}`
        : ageMinutes === null
          ? `${source}: never`
          : state === 'stale'
            ? `Mirror stale · ${formatAge(ageMinutes)}`
            : `${source}: ${formatAge(ageMinutes)}`)
  return (
    <span
      role="status"
      aria-label={label}
      title={label}
      data-liveness={state}
      className={cn(
        'inline-flex h-7 shrink-0 items-center gap-2 rounded-full px-2.5 text-xs font-medium sm:px-3',
        state === 'fresh' && 'bg-success-soft text-success',
        state === 'lagging' && 'bg-warning-soft text-warning',
        state === 'stale' && 'bg-destructive-soft text-destructive',
      )}
    >
      <span
        className={cn(
          'size-2 rounded-full',
          state === 'fresh' && 'animate-pulse-dot bg-success',
          state === 'lagging' && 'bg-warning',
          state === 'stale' && 'bg-destructive',
        )}
      />
      <span className="hidden sm:inline">{label}</span>
    </span>
  )
}

export function TopBar({
  brand,
  onOpenPalette,
  searchPlaceholder = 'Search cases, customers, contractors',
  liveness,
  right,
  leading,
  compact = false,
}: {
  brand?: React.ReactNode
  onOpenPalette: () => void
  searchPlaceholder?: string
  liveness?: React.ReactNode
  right?: React.ReactNode
  /** Phone menu button. Sits before the wordmark. */
  leading?: React.ReactNode
  compact?: boolean
}) {
  return (
    <header className="flex h-(--spacing-topbar) min-w-0 max-w-full shrink-0 items-center gap-2 border-b border-border bg-canvas px-3 sm:gap-3 sm:px-4 max-pane:h-14">
      {leading}
      <div className={cn('shrink-0', compact ? '' : 'md:w-[228px]')}>{brand ?? <Wordmark />}</div>
      <button
        type="button"
        onClick={onOpenPalette}
        aria-label="Search or run a command"
        title="Search (/) or command palette (Ctrl+K)"
        className="flex h-8 min-h-8 min-w-0 max-w-xl flex-1 cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm text-subtle transition-colors hover:border-border-strong max-pane:h-11 max-pane:min-h-11"
      >
        <Search className="size-4 shrink-0" />
        <span className="truncate">
          {compact ? (
            'Search'
          ) : (
            <>
              <span className="text-muted-foreground">/</span> <span className="hidden sm:inline">{searchPlaceholder}</span>
              <span className="sm:hidden">Search</span>
            </>
          )}
        </span>
        <Kbd keys="mod+k" className="ml-auto hidden sm:inline-flex" />
      </button>
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {liveness}
        {right}
      </div>
    </header>
  )
}
